import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '../../ComponnentsUI/common/Input';
import { Checkbox } from '../../ComponnentsUI/common/Checkbox';
import { Field, MiniSelect, NumberInput, styles } from './primitives';
import { OPTIONS } from './lib/form-config';
import { useReportData } from './lib/report-context';

export function AdminStrip() {
  const { t } = useTranslation('Report');
  const {
    patientInfo,
    studyInfo,
    screening,
    studyReportFields,
    isLoading,
    onUpdateFindings,
  } = useReportData();

  const r = studyReportFields;

  const formatDateForInput = (dateStr?: string) => {
    if (!dateStr || dateStr === 'Unknown') {
      return '';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return '';
    }
    return d.toISOString().split('T')[0];
  };

  const studyDateValue = useMemo(() => {
    const reportStudyDate =
      typeof r.study_date === 'string' ? r.study_date : undefined;
    return formatDateForInput(reportStudyDate ?? studyInfo?.studyDate);
  }, [r.study_date, studyInfo?.studyDate]);

  const [studyDateInput, setStudyDateInput] = useState(studyDateValue);

  useEffect(() => {
    setStudyDateInput(studyDateValue);
  }, [studyDateValue]);

  return (
    <div className="w-52 shrink-0 border-r border-slate-200 dark:border-gray-700 p-2 space-y-1.5 bg-slate-50/80 dark:bg-gray-800/50 text-xs">
      <Field label="admin.patientName">
        <Input
          placeholder={isLoading ? t('admin.loading') : t('admin.fullName')}
          defaultValue={
            patientInfo?.name !== 'Unknown Patient' ? patientInfo?.name : ''
          }
          className={styles.input}
        />
      </Field>
      <Field label="admin.dateOfBirth">
        <Input
          type="date"
          defaultValue={formatDateForInput(patientInfo?.birthday)}
          className={styles.input}
        />
      </Field>
      <Field label="admin.studyDate">
        <Input
          type="date"
          value={studyDateInput}
          className={styles.input}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setStudyDateInput(e.target.value)
          }
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            const nextValue = e.target.value || '';
            if (nextValue === studyDateValue) {
              return;
            }
            onUpdateFindings?.({
              study_date: nextValue || null,
            });
          }}
        />
      </Field>
      <Field label="admin.orderingClinician">
        <Input
          placeholder={t('admin.clinician')}
          defaultValue={
            r.ordering_clinician ? String(r.ordering_clinician) : ''
          }
          className={styles.input}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
            onUpdateFindings?.({
              ordering_clinician: e.target.value || null,
            })
          }
        />
      </Field>
      <Field label="admin.indications">
        <MiniSelect
          defaultValue={r.indications ? String(r.indications) : 'morphology'}
          options={OPTIONS.indications}
          onChange={(e) =>
            onUpdateFindings?.({ indications: e.target.value || null })
          }
        />
      </Field>
      <div className="grid grid-cols-2 gap-1">
        <Field label="admin.weightKg">
          <NumberInput
            step={1}
            defaultValue={
              r.maternal_weight_kg != null
                ? Number(r.maternal_weight_kg)
                : undefined
            }
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                maternal_weight_kg: e.target.value
                  ? parseFloat(e.target.value)
                  : null,
              })
            }
          />
        </Field>
        <Field label="admin.nbFetuses" explainFindingKey="pregnancy_type">
          <NumberInput
            defaultValue={r.nb_fetuses != null ? Number(r.nb_fetuses) : 1}
            min={1}
            max={4}
            step={1}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) =>
              onUpdateFindings?.({
                nb_fetuses: e.target.value
                  ? parseInt(e.target.value, 10)
                  : null,
              })
            }
          />
        </Field>
      </div>
      <Field label="admin.fetalSex" explainFindingKey="sex_prediction">
        <MiniSelect
          defaultValue={screening?.sexPrediction || 'not-assessed'}
          options={[
            { value: 'not-assessed', label: 'options.notAssessedSex' },
            { value: 'male', label: 'options.male' },
            { value: 'female', label: 'options.female' },
            { value: 'indeterminate', label: 'options.indeterminate' },
          ]}
          onChange={(e) =>
            onUpdateFindings?.({
              sex_prediction:
                e.target.value === 'not-assessed' ? null : e.target.value,
            })
          }
        />
      </Field>
      <Field label="admin.chorionicity">
        <MiniSelect
          defaultValue={r.chorionicity ? String(r.chorionicity) : 'singleton'}
          options={OPTIONS.chorionicity}
          onChange={(e) =>
            onUpdateFindings?.({ chorionicity: e.target.value || null })
          }
        />
      </Field>
      <Field label="admin.examConditions">
        <MiniSelect
          defaultValue={r.exam_conditions ? String(r.exam_conditions) : 'good'}
          options={OPTIONS.examConditions}
          onChange={(e) =>
            onUpdateFindings?.({ exam_conditions: e.target.value || null })
          }
        />
      </Field>
      <Field label="admin.examinationRoute">
        <MiniSelect
          defaultValue={r.exam_route ? String(r.exam_route) : 'abdominal'}
          options={[
            { value: 'abdominal', label: 'options.transabdominal' },
            { value: 'vaginal', label: 'options.transvaginal' },
          ]}
          onChange={(e) =>
            onUpdateFindings?.({ exam_route: e.target.value || null })
          }
        />
      </Field>
      <div className="flex items-center gap-1.5 pt-0.5">
        <Checkbox
          id="probe-d"
          defaultChecked={r.probe_disinfection_confirmed !== false}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onUpdateFindings?.({
              probe_disinfection_confirmed: e.target.checked,
            })
          }
        />
        <label
          htmlFor="probe-d"
          className="text-[10px] cursor-pointer text-slate-500 dark:text-gray-400"
        >
          {t('admin.probeDisinfection')}
        </label>
      </div>
    </div>
  );
}
